import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import useProcessingExpiryTick from 'modules/common/components/VerifiedStatementUpload/hooks/useProcessingExpiryTick';
import { StatementFile } from 'modules/common/components/VerifiedStatementUpload/components/StatementSlotList';
import toStatementVerdict, {
    isAwaitingVerdict,
} from 'modules/common/components/VerifiedStatementUpload/utils/statementVerdict';
import { STATEMENT_COVERAGE_QUERY_KEY } from 'services/bookkeeping/queries/useStatementCoverage';
import useStatementUploadStatuses from 'services/bookkeeping/queries/useStatementUploadStatuses';

/**
 * How often to ask while something is in flight.
 *
 * The slow tier: these surfaces are what a member sees when they are *not* watching a file land.
 * They share a query key with the modal's faster poll and react-query runs the shortest interval
 * any observer asks for, so opening the modal speeds these up for free.
 */
const POLL_INTERVAL_MS = 15_000;

/** Verdicts where we could not read the document at all, as opposed to read it and decline it. */
const UNREADABLE_VERDICT_KINDS: ReadonlySet<string> = new Set(['error', 'notAStatement']);

/** One drop of statements, from the first file landing to the last verdict coming back. */
interface StatementRun {
    /**
     * Distinguishes this run from the next. A counter rather than the batch's upload time, because
     * `uploadedAt` is optional and keying on it conflated "nothing dismissed" with "an undated run
     * dismissed".
     */
    readonly id: number;
    /** Documents the run carried, counting the ones already answered for. */
    readonly count: number;
    /** Whether every one of them has an answer, so the run is over. */
    readonly isFinished: boolean;
    /** Of a finished run, the documents that came back unreadable — failed, or not read as a statement. */
    readonly unreadableCount: number;
}

interface AwaitingStatements {
    /** Which of the given uploads the server has not answered for yet. */
    readonly awaitingFiles: StatementFile[];
    /** The run in flight, the one that just finished, or `null` when there has been neither. */
    readonly run: StatementRun | null;
}

/**
 * What the server still owes an answer on, and how the drop it belongs to is going.
 *
 * The one place outside the modal that decides "is this still in flight", so the banner over the
 * card list and the spinner on a card cannot disagree — they are the same claim rendered twice.
 * Polls on its own rather than reading the modal's poll, which unmounts with the modal and would
 * freeze these surfaces at whatever the last open modal saw.
 *
 * Ages a file out at `PROCESSING_EXPIRY_MS`, so a document the server never answers for stops being
 * claimed as in-flight at the same moment its own row asks the member to delete and retry.
 */
export default function useAwaitingStatements(files: readonly StatementFile[]): AwaitingStatements {
    const { data: statusByUuid } = useStatementUploadStatuses({
        enabled: files.length > 0,
        refetchInterval: POLL_INTERVAL_MS,
    });

    // Fed from every upload, not the awaiting ones: that is what the tick asks for, and it stops
    // this being circular — its input cannot be derived from the value it exists to refresh.
    const uploadedAts = useMemo(() => files.map((file) => file.uploadedAt), [files]);
    const expiryTick = useProcessingExpiryTick(uploadedAts);

    // `expiryTick` is a dependency because the clock is one. A file the server never answers for
    // returns the same payload every poll, so react-query hands back the same object and nothing
    // re-renders; without the tick this memo keeps the set it computed before the expiry boundary.
    const awaitingFiles = useMemo(
        () =>
            files.filter((file) =>
                isAwaitingVerdict(toStatementVerdict(statusByUuid?.[file.documentUuid]), file.uploadedAt)
            ),
        [files, statusByUuid, expiryTick]
    );

    /**
     * Refresh coverage when a file this hook was watching is filed.
     *
     * Coverage is never polled — a read costs a Tight plus Plaid round trip — so it is refreshed on
     * the transitions that change the answer, and this is the one nothing owned: the modal refreshes
     * on settle but only while open, and the step's close handler fires when the modal is dismissed,
     * which on a batch still being read is before the answer can have moved.
     *
     * `matched` only, because only a filed statement mints the tracker rows coverage reports.
     * Watched-then-left rather than "is matched now", or a member arriving on statements filed weeks
     * ago would refresh once for each of them on mount.
     */
    const watchedUuidsRef = useRef<ReadonlySet<string>>(new Set());
    const queryClient = useQueryClient();

    useEffect(() => {
        const stillAwaiting = new Set(awaitingFiles.map((file) => file.documentUuid));
        // Deleted files are excluded rather than counted as filed: a delete moves held months
        // itself and invalidates coverage on its own.
        const presentUuids = new Set(files.map((file) => file.documentUuid));

        const hasNewlyFiled = [...watchedUuidsRef.current].some(
            (uuid) =>
                presentUuids.has(uuid) &&
                !stillAwaiting.has(uuid) &&
                toStatementVerdict(statusByUuid?.[uuid]).kind === 'matched'
        );

        watchedUuidsRef.current = stillAwaiting;

        if (hasNewlyFiled) {
            queryClient.invalidateQueries({ queryKey: STATEMENT_COVERAGE_QUERY_KEY });
        }
    }, [files, awaitingFiles, statusByUuid, queryClient]);

    /**
     * Follow the drop as a whole, so a caller can confirm it once the reading behind it is over.
     *
     * Tied to the work rather than the transfer: confirming when the files reached the server put
     * "uploaded successfully" over a dropzone still saying "Uploading 7 statements...". The run
     * accumulates every file watched since the last empty set, so a second drop landing on one still
     * being read is confirmed once, for both.
     *
     * Only files the server actually answered for are counted. A file can leave the awaiting set by
     * ageing out instead, and telling a member a document went through because we gave up waiting is
     * the one thing this must never do — so a run of those confirms nothing, and a run that lost one
     * confirms the rest. Deleted files are dropped for the same reason.
     */
    const runUuidsRef = useRef(new Set<string>());
    const runIdRef = useRef(0);
    const [run, setRun] = useState<StatementRun | null>(null);

    useEffect(() => {
        if (awaitingFiles.length > 0) {
            if (runUuidsRef.current.size === 0) {
                runIdRef.current += 1;
            }
            awaitingFiles.forEach((file) => runUuidsRef.current.add(file.documentUuid));
            const count = runUuidsRef.current.size;
            // Held by identity where nothing has changed, because this is a dependency of the
            // caller's own effects — a fresh object on every poll would restart their timers.
            setRun((current) =>
                current !== null && current.id === runIdRef.current && current.count === count && !current.isFinished
                    ? current
                    : { id: runIdRef.current, count, isFinished: false, unreadableCount: 0 }
            );
            return;
        }

        if (runUuidsRef.current.size === 0) {
            return;
        }
        const presentUuids = new Set(files.map((file) => file.documentUuid));
        const answered = [...runUuidsRef.current].filter(
            (uuid) => presentUuids.has(uuid) && toStatementVerdict(statusByUuid?.[uuid]).kind !== 'processing'
        );
        runUuidsRef.current = new Set();

        const unreadableCount = answered.filter((uuid) =>
            UNREADABLE_VERDICT_KINDS.has(toStatementVerdict(statusByUuid?.[uuid]).kind)
        ).length;
        setRun(
            answered.length > 0
                ? { id: runIdRef.current, count: answered.length, isFinished: true, unreadableCount }
                : null
        );
    }, [files, awaitingFiles, statusByUuid]);

    return { awaitingFiles, run };
}
